<template>
    <div :class="props.class">
        <ConfirmationModal v-if="askConfirmation" :title="confirmationTitle" :text="confirmationText"
            @emitResponse="checkConfirmationResponse" />
        <LoadingDialog v-if="savingModuleVideo" />

        <Dialog v-model:visible="moduleVideoSaveVisible" modal
            :header="selectedModuleVideo.id == null ? 'Criar vídeo' : 'Editar vídeo'" :draggable="false"
            :style="{ width: '50rem' }">
            <div class="flex items-center gap-4 mb-8">
                <label for="description" class="font-semibold w-24">Descrição
                    <RequiredMarker :isValid="dataToValidate.description" />
                </label>
                <InputText v-model="selectedModuleVideo.description" id="description"
                    class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500" />
            </div>
            <template v-if="!loadingVideo">
                <div class="flex items-center gap-4 mb-8">
                    <label for="videoFile" class="font-semibold w-24">Vídeo</label>
                    <div class="flex justify-between w-full">
                        <FileUpload name="video" accept="video/*" mode="basic" :maxFileSize="maxFileSize"
                            :chooseLabel="selectedModuleVideo.video_path != null ? 'Alterar vídeo' : 'Adicionar vídeo'"
                            @select="onSelect" class="bg-passion-fruit-forced" />
                        <Button v-if="selectedModuleVideo.video_path != null && selectedModuleVideo.active" label="Ver vídeo" icon="pi pi-play"
                            class="no-bg-button" @click="fetchVideo(selectedModuleVideo.id)" />
                        <p class="my-auto underline text-candy" v-if="!selectedModuleVideo.active && selectedModuleVideo.id != null">
                            Vídeo em processamento!
                        </p>
                    </div>
                </div>
                <div class="flex justify-end gap-2">
                    <Button label="Cancel" severity="secondary" @click="moduleVideoSaveVisible = false" :disabled="savingModuleVideo"></Button>
                    <Button class="no-bg-button" label="Save" :loading="savingModuleVideo" :disabled="savingModuleVideo" @click.prevent.stop="saveModuleVideo()"></Button>
                </div>
            </template>
            <template v-else>
                <LoaderComponent />
            </template>
        </Dialog>

        <Dialog v-model:visible="videoPreviewVisible" modal :header="'Preview'" :draggable="false"
            :style="{ width: '50rem' }" @hide="onVideoPreviewDialogClose()">
            <div class="w-full">
                <video ref="videoPlayer" controls preload="auto" autoplay muted
                    class="mx-auto w-full" style="max-height: 350px" />
            </div>
        </Dialog>

        <div class="px-2" v-if="loadingModuleVideos">
            <LoaderComponent />
        </div>
        <p class="font-semibold text-2xl mb-10">
            Vídeos do módulo: <span class="text-passion-fruit underline">{{ getTitleOfModule() }}</span>
        </p>
        <div class="w-full text-left mb-5">
            <Button :label="'Novo vídeo'" class="no-bg-button" @click.prevent="openSaveModuleVideo(null)" />
        </div>
        <div v-if="!loadingModuleVideos" class="draggable-list">
            <template v-for="(moduleVideoData, index) of moduleVideosData" :key="index">
                <div class="p-2 my-2 border border-iron rounded flex justify-between draggable-item text-white font-semibold flex-wrap flex-col md:flex-nowrap md:flex-row"
                    :class="{ dragging: index === draggingIndex }" @mousedown="onDragStart(index, $event)"
                    @mousemove="onDragMove($event)" @mouseup="onDragEnd">
                    <div class="w-full md:w-auto">
                        {{ moduleVideoData.description }}
                    </div>
                    <div
                        class="flex flex-wrap justify-center md:justify-between w-full md:w-auto md:flex-row mt-5 mb:mt-0">
                        <Button class="no-bg-button" :icon="'pi pi-pen-to-square'"
                            @click="openSaveModuleVideo(moduleVideoData.id)" v-tooltip.top="'Editar vídeo'" />
                        <Button class="no-bg-button ml-3" :icon="'pi pi-file-edit'"
                            v-tooltip.top="'Gerenciar materiais de apoio'"
                            @click.prevent="openModuleVideoResourseSection(moduleVideoData.id)" />
                        <Button class="no-bg-button mx-3" :icon="'pi pi-pencil'" v-tooltip.top="'Gerenciar atividade'"
                            @click.prevent="openModuleVideoLessonSection(moduleVideoData)" />
                        <Button class="no-bg-button" icon="pi pi-trash" v-tooltip.top="'Remover vídeo'"
                            @click.prevent="openConfirmationModal('removeCourseModuleVideo', moduleVideoData.id)" />
                    </div>
                </div>
            </template>
        </div>

        <ModulesVideosResoursesComponent :videoId="videoIdOfResourses" @closed="videoIdOfResourses = null" />
    </div>
</template>

<script setup>
import { defineProps, defineEmits, onMounted, reactive, ref, watch, onBeforeUnmount, nextTick } from 'vue';
import { useToast } from 'primevue/usetoast';
import { showToast } from '@/composables/toast';
import utils from '@/composables/utils';
import LoaderComponent from '@/components/Utils/LoaderComponent.vue';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import RequiredMarker from '@/components/misc/RequiredMarkerComponent.vue';
import { isTextValid } from '@/composables/textUtils';
import LoadingDialog from '@/components/Utils/LoadingDialog.vue';
import ConfirmationModal from '@/components/Utils/ConfirmationModal.vue';
import FileUpload from 'primevue/fileupload';
import 'video.js/dist/video-js.css';
import cookiesHandler from '@/composables/cookiesHandler';
import ModulesVideosResoursesComponent from './ModulesVideosResoursesComponent.vue';
import dashjs from 'dashjs';
import CourseVideoService from '@/services/CourseVideoService';
import { translate } from '@/composables/translate';

const emit = defineEmits(["actionPerformed"]);
const toast = useToast();
const props = defineProps({
    moduleData: {
        type: Object,
        required: true
    },
    class: {
        type: String,
        required: false,
        default: ''
    }
});

const maxFileSize = 1 * 1024 * 1024 * 1024;
const selectedFile = ref(null);
let moduleVideoSaveVisible = ref(false);
let selectedModuleVideo = ref({
    id: null,
    title: null,
    description: null,
    timeInMinutes: 0,
    videoFile: null
});
let savingModuleVideo = ref(false);
let loadingModuleVideos = ref(true);
let moduleVideosData = ref([]);
let moduleData = ref(null);
let loadingVideo = ref(false);

onMounted(() => {
    moduleData.value = props.moduleData;
    getModuleVideos();
});

watch(() => props.moduleData, (newValue) => {
    moduleData.value = newValue;
    getModuleVideos();
}, { deep: true });

let moduleVideoById = ref({});
async function getModuleVideos() {
    if ([undefined, false, null].includes(moduleData.value?.id)) {
        showToast(toast, 'no module selected');
        return;
    }

    loadingModuleVideos.value = true;
    const videos = await CourseVideoService.getVideoByModule(moduleData.value.id);
    loadingModuleVideos.value = false;

    moduleVideosData.value = videos;

    moduleVideoById.value = {};
    for (let videoObj of moduleVideosData.value) {
        moduleVideoById.value[videoObj.id] = videoObj;
    }
}

function getTitleOfModule() {
    if (moduleData.value == null) return '';
    return moduleData.value.title;
}

const draggingIndex = ref(null);
const placeholderIndex = ref(null);
const isDragging = ref(false);
let preClickmoduleVideosData = ref();
const onDragStart = (index) => {
    preClickmoduleVideosData.value = utils.jsonEncode(utils.copyVariable(moduleVideosData.value));
    draggingIndex.value = index;
    isDragging.value = true;
    placeholderIndex.value = index;
    document.addEventListener('mousemove', onDragMove);
    document.addEventListener('mouseup', onDragEnd);
};

const onDragMove = (event) => {
    if (!isDragging.value) return;
    const { clientY } = event;
    const itemsArray = document.querySelectorAll('.draggable-item');
    itemsArray.forEach((item, index) => {
        const rect = item.getBoundingClientRect();
        if (clientY > rect.top && clientY < rect.bottom) {
            placeholderIndex.value = index;
        }
    });
};

const onDragEnd = () => {
    if (!isDragging.value) return;
    const movedItem = moduleVideosData.value.splice(draggingIndex.value, 1)[0];
    moduleVideosData.value.splice(placeholderIndex.value, 0, movedItem);
    draggingIndex.value = null;
    placeholderIndex.value = null;
    isDragging.value = false;
    document.removeEventListener('mousemove', onDragMove);
    document.removeEventListener('mouseup', onDragEnd);
    if (JSON.stringify(preClickmoduleVideosData.value) !== JSON.stringify(moduleVideosData.value)) {
        persistModulesOrder();
    }
};

async function persistModulesOrder() {
    if (savingModuleVideo.value) return;
    savingModuleVideo.value = true;
    let currModules = utils.copyVariable(moduleVideosData.value);
    let reoderedModuleVideos = [];
    for (let thisModule of currModules) {
        delete thisModule.files;
        reoderedModuleVideos.push(thisModule);
    }
    let dataToSend = { moduleVideos: reoderedModuleVideos };
    const response = await CourseVideoService.updateVideoOrder(dataToSend); 

    savingModuleVideo.value = false;
    if (!response.success()) {
        showToast(toast, response.getMessage(), 'error');
        return;
    }

    showToast(toast, response.getMessage());
}

let dataToValidate = reactive({
    description: true
})
let errorMessages = {
    description: 'Por favor informe a descrição do video'
}

async function saveModuleVideo() {
    if (savingModuleVideo.value) return;

    let isValid = true;
    for (let attr of Object.keys(dataToValidate)) {
        if (!isTextValid(selectedModuleVideo.value[attr])) {
            isValid = false;
            dataToValidate[attr] = false;
            showToast(toast, errorMessages[attr], 'error');
            break;
        } else {
            dataToValidate[attr] = true;
        }
    }
    if (!isValid) return;

    savingModuleVideo.value = true;
    loadingVideo.value = true;

    let moduleVideoData = utils.copyVariable(selectedModuleVideo.value);
    moduleVideoData['course_module'] = moduleData.value.id;
    
    if (moduleVideoData.id !== null && moduleVideoData.id !== undefined) {
        moduleVideoData.id = Number(moduleVideoData.id);
    }

    let response = { success: false, message: 'Um erro ocorreu' };
    response = await CourseVideoService.saveVideo(moduleVideoData);

    if (!response.success()) {
        showToast(toast, response.getMessage(), 'error');
        savingModuleVideo.value = false;
        loadingVideo.value = false;
        return;
    }
    
    if (selectedFile.value != null) {
        const formData = new FormData();
        formData.append('id', Number(response.getContent().id || 0));
        formData.append('videoFile', selectedFile.value);

        const uploadResponse = await CourseVideoService.saveVideoFile(formData, Number(response.getContent().id || 0));
        
        if (!uploadResponse.success()) {
            showToast(toast, 'Um erro ocorreu ao realizar o upload do vídeo', 'error');
            savingModuleVideo.value = false;
            loadingVideo.value = false;
            return;
        }
    }

    loadingVideo.value = false;
    savingModuleVideo.value = false;
    showToast(toast, selectedModuleVideo.value.id != null ? 'Vídeo atualizado' : 'Vídeo criado');
    moduleVideoSaveVisible.value = false;
    getModuleVideos();
}

let askConfirmation = ref(false);
let selectedConfirmation = ref('');
let confirmationMethodVariable = ref('');
let confirmationTitle = ref('');
let confirmationText = ref('');
let confirmationModalValues = ref({
    removeCourseModuleVideo: {
        title: 'Are you sure you wish to remove this course module?',
        text: 'this action can not be undone'
    }
})
function openConfirmationModal(action = 'removeCourseModuleVideo', identifier = '') {
    confirmationTitle.value = confirmationModalValues.value[action].title;
    confirmationText.value = confirmationModalValues.value[action].text;
    selectedConfirmation.value = action;
    confirmationMethodVariable.value = identifier;
    askConfirmation.value = true;
}
function checkConfirmationResponse(value) {
    if ([null, undefined].includes(value)) return;
    const confirmation = value.confirmation;
    askConfirmation.value = false;
    if (confirmation) {
        switch (selectedConfirmation.value) {
            case 'removeCourseModuleVideo':
                removeCourseModuleVideo(confirmationMethodVariable.value);
                break;
        }
    }
}

async function removeCourseModuleVideo(id = '') {
    if (savingModuleVideo.value) return;
    savingModuleVideo.value = true;

    const response = await CourseVideoService.deleteVideo(id);
    savingModuleVideo.value = false;

    if (!response.success()) {
        showToast(toast, response.getMessage(), 'error');
        return;
    }

    showToast(toast, translate('video removed', 'ucfirst'));
    getModuleVideos();
}

let hasAlreadyPlayerdVideo = ref();
async function openSaveModuleVideo(id = '') {
    moduleVideoSaveVisible.value = true;
    selectedFile.value = null;
    if (id === null || moduleVideoById.value[id] == undefined) {
        resetSelectedModuleVideo();
        return;
    }

    selectedModuleVideo.value = utils.copyVariable(moduleVideoById.value[id]);
    hasAlreadyPlayerdVideo.value = false;
}

function resetSelectedModuleVideo() {
    selectedModuleVideo.value = {
        id: null,
        title: null,
        description: null,
        timeInMinutes: 0,
        videoFile: null
    }
}

function onSelect(event) {
    selectedFile.value = event.files[0];
    if (selectedFile.value == undefined) {
        showToast(toast, "Um problema ocorreu ao selecionar o arquivo, tente novamente", 'error');
        selectedFile.value = null;
    } else if (selectedFile.value.size > maxFileSize) {
        showToast(toast, "O tamanho do vídeo não pode exceder 1 GB.", 'error');
        selectedFile.value = null;
    }
    selectedModuleVideo.value.videoFile = [null, undefined, ''].includes(selectedFile.value) ? null : selectedFile.value;
}

let selectedVideoId = ref(null);
let videoPreviewVisible = ref(false);
async function fetchVideo(videoId = '') {
    selectedVideoId.value = videoId;
    videoPreviewVisible.value = true;
}

onBeforeUnmount(() => {
    destroyPlayer();
});

watch(videoPreviewVisible, (isVisible) => {
    if (isVisible) {
        playVideo();
    } else {
        destroyPlayer();
    }
});

const videoPlayer = ref(null);
const player = ref(null);
let hasAlreadyPlayedVideo = ref(false);

async function playVideo() {
    if (hasAlreadyPlayedVideo.value || !selectedVideoId.value) return;
    
    await nextTick(); 

    if (!videoPlayer.value) return;

    hasAlreadyPlayedVideo.value = true;

    const available = await CourseVideoService.isVideoAvaliable(selectedVideoId.value);
    if (!available) {
        showToast(toast, 'Vídeo em processamento...', 'info');
        videoPreviewVisible.value = false;
        return;
    }

    destroyPlayer();

    player.value = dashjs.MediaPlayer().create();
    
    player.value.extend("RequestModifier", () => ({
        modifyRequestHeader: (xhr) => {
            xhr.setRequestHeader("Authorization", `Bearer ${cookiesHandler.get('TM_TOKEN')}`);
            return xhr;
        },
        modifyRequestURL: (url) => url,
    }), true);

    player.value.initialize(
        videoPlayer.value, 
        `${process.env.VUE_APP_API_URL}api/video/${selectedVideoId.value}/play`, 
        true
    );

    videoPlayer.value.muted = false;
    videoPlayer.value.volume = 0.5;
}

function destroyPlayer() {
    if (player.value) {
        player.value.pause();
        player.value.reset();
        player.value.destroy();
        player.value = null;
    }
    hasAlreadyPlayedVideo.value = false;
}

function onVideoPreviewDialogClose() {
    destroyPlayer();
    selectedVideoId.value = null;
    hasAlreadyPlayerdVideo.value = false;
}

let videoIdOfResourses = ref(null);
function openModuleVideoResourseSection(id = null) {
    videoIdOfResourses.value = id;
}

function openModuleVideoLessonSection(videoData = {}) {
    emit('lessonsSelected', videoData);
}
</script>

<style scoped>
.draggable-item {
    background-color: var(--passion-fruit) !important;
}

.no-bg-button {
    border: 1px solid var(--passion-fruit) !important;
    background-color: #fff !important;
    color: var(--passion-fruit) !important;
}

.no-bg-button:hover {
    border: 1px solid var(--dark-passion-fruit) !important;
    color: var(--dark-passion-fruit) !important;
    transition: 0.2s ease;
}
</style>